// What is left on an order after items were cancelled on Shopify, and the
// order flags Ops needs to see.
//
// The backend marks units cancelled on Shopify before shipping on each line
// (`cancelled_quantity`, `current_quantity`) and keeps the ordered `quantity`
// unchanged for accounts. `meta.current_totals` holds the value of what is left.
// See suspire-server orders/order_cancellation_sync.py and orders/order_alerts.py.

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const cancelledQuantity = (item) => Math.max(0, toNumber(item?.cancelled_quantity));

export const remainingQuantity = (item) => {
  if (item && item.current_quantity !== undefined && item.current_quantity !== null) {
    return Math.max(0, toNumber(item.current_quantity));
  }
  return Math.max(0, toNumber(item?.quantity));
};

// Payable value of what is left on a line: remaining units at the line price,
// minus the Suspire discount prorated to those units.
export const remainingLineValue = (item) => {
  const ordered = toNumber(item?.quantity, 1) || 1;
  const remaining = remainingQuantity(item);
  const price = toNumber(item?.price);
  const discount = toNumber(item?.suspire_discount_amount) * (remaining / ordered);
  return Math.max(0, price * remaining - discount);
};

// The remaining order value when items were cancelled, else null.
export const remainingOrderValue = (apiOrder) => {
  const totals = apiOrder?.meta?.current_totals;
  if (!totals || totals.net_payable === undefined) return null;
  return toNumber(totals.net_payable);
};

// Shopify's current payment state, as the two-state badge/filter value the
// Terminal uses (financial_status is only the state at order creation).
export const paymentBadge = (apiOrder) => {
  const status = String(apiOrder?.payment_status || apiOrder?.financial_status || '').toLowerCase();
  return status === 'paid' || status === 'partially_refunded' ? 'PAID' : 'PENDING';
};

const FLAG_TEXT = {
  paid_without_payment: {
    tone: 'error',
    title: 'Marked paid, but no payment was captured',
    message: 'This COD order was created as paid on Shopify. Labels are blocked until it is resolved.',
  },
  short_payment: {
    tone: 'error',
    title: 'Paid short of the order total',
    message: 'The customer paid less than the order value. Labels are blocked until the balance is resolved on Shopify.',
  },
  payment_not_settled: {
    tone: 'warning',
    title: 'Payment not settled',
    message: 'No gateway settlement contains this payment yet. Check it in the gateway dashboard.',
  },
  label_stale_items_cancelled: {
    tone: 'warning',
    title: 'Label lists cancelled items',
    message: 'Items were cancelled on Shopify after this label was generated. Cancel and regenerate the label.',
    label: true,
  },
  label_on_cancelled_order: {
    tone: 'warning',
    title: 'Label on a cancelled order',
    message: 'Every item was cancelled on Shopify after this label was generated. Cancel the label.',
    label: true,
  },
  vendor_store_order_items_cancelled: {
    tone: 'warning',
    title: "Update the vendor's store order",
    message: "Items were cancelled on Shopify; remove them from the order on the vendor's own store.",
  },
  address_changed_after_label: {
    tone: 'warning',
    title: 'Address changed after the label',
    message: "The customer's address or phone was changed on Shopify after this label was generated. Cancel and regenerate the label.",
    label: true,
  },
  vendor_store_address_sync_failed: {
    tone: 'warning',
    title: "Vendor's store order still has the old address",
    message: "The new address could not be pushed to the vendor's own store. Update it there.",
  },
};

// Open flags to show on the order, newest knowledge first. A label flag only
// shows while the label in use was generated before the cancellation -- once
// Ops cancels or regenerates it, the warning goes away on its own.
export const openOrderFlags = (apiOrder) => {
  const flags = apiOrder?.risk_flags || {};
  const label = apiOrder?.shipment;
  const labelAt = label?.label_generated_at ? new Date(label.label_generated_at) : null;
  return Object.entries(FLAG_TEXT)
    .filter(([key, text]) => {
      const entry = flags[key];
      if (!entry || entry.status !== 'open') return false;
      if (!text.label) return true;
      if (!label || !(label.label_url || label.awb_number)) return false;
      const raisedAt = entry.raised_at ? new Date(entry.raised_at) : null;
      return !labelAt || !raisedAt || labelAt <= raisedAt;
    })
    .map(([key, text]) => ({ key, ...text, details: flags[key].details || {} }));
};
